import { isDefined } from "@clipboard-health/util-ts";
import { useTheme } from "@mui/material";
import { useCurrentDate } from "@src/appV2/lib/utils/useCurrentDate";
import { CircleProgress, type UseModalState } from "@src/appV2/redesign/system/ui-components";
import { isRebrandingEnabled } from "@src/appV2/redesign/system/ui-theme";
import { differenceInMilliseconds, isBefore, startOfSecond } from "date-fns";

import { ShiftCircleProgressContent } from "./Content";
import { PreregistrationCountdownDisplay } from "./PreregistrationCountdownDisplay";
import { getShiftProgress } from "./utils/getShiftProgress";

const MINUTES_TO_MS = 60 * 1000;

interface ShiftCircleProgressProps {
  startDate: Date;
  endDate: Date;
  clockInDate?: Date;
  adjustedClockInDate?: Date;
  includeDatePill?: boolean;
  clockOutModalState: UseModalState;
  preregistrationWindowInMinutes?: number;
  /** When provided, freezes the timer at this time instead of using the live clock. */
  frozenTime?: Date;
}

export function ShiftCircleProgress(props: ShiftCircleProgressProps) {
  const {
    startDate,
    endDate,
    clockInDate,
    adjustedClockInDate,
    includeDatePill = false,
    clockOutModalState,
    preregistrationWindowInMinutes,
    frozenTime,
  } = props;

  const currentDate = useCurrentDate(1000);
  const theme = useTheme();

  if (!isDefined(clockInDate)) {
    return null;
  }

  const now = isDefined(frozenTime) ? startOfSecond(frozenTime) : startOfSecond(currentDate);

  if (isDefined(preregistrationWindowInMinutes) && isBefore(now, startDate)) {
    const windowMs = preregistrationWindowInMinutes * MINUTES_TO_MS;
    const remainingMs = differenceInMilliseconds(startDate, now);
    const countdownProgress = Math.min(remainingMs / windowMs, 1);
    const rotation = -(countdownProgress * 360);

    return (
      <CircleProgress
        value={countdownProgress}
        rotation={rotation}
        color={theme.palette.shift?.urgent.backgroundInvert}
      >
        <PreregistrationCountdownDisplay startDate={startDate} now={now} />
      </CircleProgress>
    );
  }

  const {
    progress,
    secondaryProgress,
    timeWorked,
    timeLeft,
    isOvertime,
    overtimeText,
    totalTimeWorked,
    pendingProgress,
  } = getShiftProgress({
    clockInDate,
    adjustedClockInDate,
    shiftStartDate: startDate,
    shiftEndDate: endDate,
    now,
  });

  const hasExceededOvertimeThreshold = isOvertime && progress >= 1;

  function getPrimaryColor(): string | undefined {
    if (isRebrandingEnabled()) {
      if (hasExceededOvertimeThreshold) {
        return theme.palette.intent?.error.border;
      }

      if (isOvertime) {
        return theme.palette.intent?.warning.border;
      }

      return theme.palette.success.light;
    }

    if (hasExceededOvertimeThreshold) {
      return theme.palette.intent?.warning.border;
    }

    return theme.palette.text.primary;
  }

  return (
    <CircleProgress
      value={progress}
      color={getPrimaryColor()}
      secondaryValue={isOvertime ? secondaryProgress : undefined}
      secondaryColor={theme.palette.text.primary}
      pendingValue={isOvertime ? undefined : pendingProgress}
      pendingSecondaryValue={isOvertime ? pendingProgress : undefined}
      pendingColor={theme.palette.intent?.warning.border}
    >
      <ShiftCircleProgressContent
        hasExceededOvertimeThreshold={hasExceededOvertimeThreshold}
        includeDatePill={includeDatePill}
        shiftStartDate={startDate}
        isOvertime={isOvertime}
        progress={progress}
        overtimeText={overtimeText}
        totalTimeWorked={totalTimeWorked}
        timeWorked={timeWorked}
        timeLeft={timeLeft}
        clockOutModalState={clockOutModalState}
      />
    </CircleProgress>
  );
}
